import { phone as formatPhone } from "@repo/helpers";
import { GridColumnTemplateEnum } from "@repo/models";
import { ButtonColor, ButtonColumn, Column, DateColumn, LocalDateColumn, OriginalTimezoneDateColumn } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface BackendColumn {
	gridColumnId: number;
	name: string;
	title: string;
	position: number;
	visible: boolean;
	width: number;
	sortable: boolean;
	filterable: boolean;
	exportable?: boolean;
	template?: string;
	templateId?: number;
	options?: {
		text?: string | null;
		onClick?: string | null;
		dataType?: string | null;
		dateType?: string | null;
		format?: string | null;
		type?: string;
		options?: string[];
		visible?: string | null;
		color?: ButtonColor;
	};
}

interface BackendGrid {
	gridId: number;
	name: string;
	columns: BackendColumn[];
}

export function useGridColumns(
	gridName: string,
	getGridColumnsFn: () => Promise<BackendGrid>,
	callbacks?: Record<string, Function>,
	settings?: Record<string, any>,
) {
	const { data, isLoading, isError } = useQuery({
		queryKey: ["GridColumns", gridName],
		queryFn: () => getGridColumnsFn(),
	});

	const columns = useMemo(() => {
		if (!data?.columns) return [];
		return data.columns.map((col: BackendColumn) => {
			const column = mapColumn(col, callbacks);

			if (col.options?.visible) {
				const isVisible = settings?.[col.options.visible];
				if (!isVisible) {
					column.withVisibility(false);
				}
			}

			column.withVisibility(col.visible);

			if (col.width) column.withMinWidth(col.width);

			column.withSortable(col.sortable);
			column.withExportable(col.exportable ?? true);

			return column;
		});
	}, [data, settings, callbacks]);

	return { columns, isLoading: isLoading && !isError };
}

function getNestedValue(obj: any, path: string) {
	return path.split(".").reduce((prev, curr) => (prev as any)?.[curr], obj);
}

function mapColumn<T>(col: BackendColumn, callbacks?: Record<string, Function>): Column<T> {
	switch (col.templateId) {
		case GridColumnTemplateEnum.Date:
			switch (col.options?.dateType) {
				case "original":
					return new OriginalTimezoneDateColumn<T>(col.title, col.name).dateOnly();
				case "local":
					return new LocalDateColumn<T>(col.title, col.name).dateOnly();
				default:
					return new DateColumn<T>(col.title, col.name).dateOnly();
			}
		case GridColumnTemplateEnum.DateTime:
			switch (col.options?.dateType) {
				case "original":
					return new OriginalTimezoneDateColumn<T>(col.title, col.name);
				case "local":
					return new LocalDateColumn<T>(col.title, col.name);
				default:
					return new DateColumn<T>(col.title, col.name);
			}
		case GridColumnTemplateEnum.Boolean:
			if (col.options.options) {
				const [trueValue, falseValue, nullValue] = col.options.options;
				return new Column<T>(col.title, col.name).withBooleanFormat(trueValue, falseValue, nullValue);
			} else {
				return new Column<T>(col.title, col.name).withBooleanFormat();
			}
		case GridColumnTemplateEnum.PhoneNumber:
			return new Column<T>(col.title, col.name).withFormat((v) => formatPhone(v));
		case GridColumnTemplateEnum.LinkButton:
			return new Column<T>(col.title, col.name).asLink("");
		case GridColumnTemplateEnum.Button: {
			const btn = new ButtonColumn<T>(col.title, col.name);
			if (col.options?.text) {
				btn.withButtonText(col.options.text);
			}
			if (col.options?.onClick && callbacks?.[col.options.onClick]) {
				const callback = callbacks[col.options.onClick];
				btn.withOnClick((row) => {
					// Most of our grid buttons take an ID, but some might want the full row.
					// Based on the example "openPortalModal(locationId: number)", it expects an ID.
					// We'll try to find a suitable ID property if it's not the name.
					const val = col.name ? getNestedValue(row, col.name) : row;
					callback(val);
				});
			}
			if (col.options.color) {
				btn.withButtonColor(col.options.color);
			}
			return btn;
		}
		case GridColumnTemplateEnum.Percentage:
			return new Column<T>(col.title, col.name).withFormat((row) => `${((row ?? 0) * 100)?.toFixed(0)}%`);
		default:
			return new Column<T>(col.title, col.name);
	}
}
